import ApiClient from "@/services/api/apiClient";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import {
  TENANT_LOOKUP_ENDPOINT,
  TENANT_LOOKUP_PARAMS,
} from "../constants/tenantLookup";
import type {
  InstitutionDetail,
  InstitutionsListParams,
  InstitutionsListResult,
  InstitutionWritePayload,
  TenantOption,
} from "../types/institution.types";
import {
  asRecord,
  mapInstitutionApiItemToDetail,
  mapInstitutionApiItemToRow,
  mapInstitutionsPagination,
  mapTenantOptions,
  unwrapInstitutionDetailBody,
  unwrapInstitutionsListBody,
} from "../utils/institutionMappers";

/** Wire names of the list query params. Single place to change if the backend differs. */
export const INSTITUTION_LIST_QUERY_PARAMS = {
  search: "search",
  status: "status",
  page: "page",
  pageSize: "page_size",
  tenantId: "tenant_id",
} as const;

/** HTTP verb used for updates (PATCH /api/v1/institutions/:id, mirrors tenants). */
export const INSTITUTION_UPDATE_METHOD: "put" | "patch" = "patch";

/** The API client returns the raw body; only an explicit `success: false` is a failure. */
function assertNotFailed(body: unknown, fallbackMessage: string): void {
  const rec = asRecord(body);
  if (rec && rec.success === false) {
    throw new Error(
      typeof rec.message === "string" && rec.message
        ? rec.message
        : fallbackMessage,
    );
  }
}

function requireId(id: string): string {
  const trimmed = (id ?? "").trim();
  if (!trimmed) throw new Error("Institution id is missing");
  return trimmed;
}

export async function fetchInstitutionsList(
  params: InstitutionsListParams,
): Promise<InstitutionsListResult> {
  const query: Record<string, string | number> = {
    [INSTITUTION_LIST_QUERY_PARAMS.page]: params.page,
    [INSTITUTION_LIST_QUERY_PARAMS.pageSize]: params.pageSize,
  };
  const search = params.search?.trim();
  if (search) query[INSTITUTION_LIST_QUERY_PARAMS.search] = search;
  const status = params.status?.trim();
  if (status) query[INSTITUTION_LIST_QUERY_PARAMS.status] = status;
  const tenantId = params.tenantId?.trim();
  if (tenantId) query[INSTITUTION_LIST_QUERY_PARAMS.tenantId] = tenantId;

  const body: unknown = await ApiClient.get(
    API_ENDPOINTS.INSTITUTIONS.LIST,
    query,
  );
  assertNotFailed(body, "Failed to load institutions");
  const { items, pagination } = unwrapInstitutionsListBody(body);
  const rows = items.map(mapInstitutionApiItemToRow);
  return {
    rows,
    pagination: mapInstitutionsPagination(pagination, {
      page: params.page,
      pageSize: params.pageSize,
      rowCount: rows.length,
    }),
  };
}

export async function getInstitution(id: string): Promise<InstitutionDetail> {
  const institutionId = requireId(id);
  const body: unknown = await ApiClient.get(
    API_ENDPOINTS.INSTITUTIONS.DETAIL(institutionId),
  );
  assertNotFailed(body, "Failed to load institution");
  const item = unwrapInstitutionDetailBody(body);
  if (!item) throw new Error("Institution not found");
  return mapInstitutionApiItemToDetail(item, institutionId);
}

/** `payload` comes from buildInstitutionPayload and is sent as is. */
export async function createInstitution(
  payload: InstitutionWritePayload,
): Promise<void> {
  const body: unknown = await ApiClient.post(
    API_ENDPOINTS.INSTITUTIONS.CREATE,
    payload,
  );
  assertNotFailed(body, "Failed to create institution");
}

export async function updateInstitution(
  id: string,
  payload: InstitutionWritePayload,
): Promise<void> {
  const institutionId = requireId(id);
  // The tenant of an institution cannot be changed, so tenant_id is not sent on update.
  const { tenant_id: _tenantId, ...updateBody } = payload;
  void _tenantId;
  const body: unknown = await ApiClient[INSTITUTION_UPDATE_METHOD](
    API_ENDPOINTS.INSTITUTIONS.UPDATE(institutionId),
    updateBody,
  );
  assertNotFailed(body, "Failed to update institution");
}

export async function deleteInstitution(id: string): Promise<void> {
  const institutionId = requireId(id);
  const body: unknown = await ApiClient.delete(
    API_ENDPOINTS.INSTITUTIONS.DELETE(institutionId),
  );
  assertNotFailed(body, "Failed to delete institution");
}

/** Active tenants (first TENANT_LOOKUP_LIMIT) for the form's tenant dropdown. */
export async function fetchTenantOptions(): Promise<TenantOption[]> {
  const body: unknown = await ApiClient.get(TENANT_LOOKUP_ENDPOINT, {
    ...TENANT_LOOKUP_PARAMS,
  });
  assertNotFailed(body, "Failed to load tenants");
  return mapTenantOptions(body);
}
